/**
 * Componente FloatingHelpButton
 * 
 * Pulsante flottante in basso a destra per accedere rapidamente alla guida.
 * Mostra un menu con:
 * - Link alla guida completa
 * - Categorie principali
 * - Form per inviare una domanda
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HelpCircle,
  BookOpen,
  MessageCircleQuestion,
  ChevronUp,
  Send,
  Loader2,
  Rocket,
  GraduationCap,
  Settings,
  FileEdit,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useGuideCategories, useSubmitQuestion } from "@/hooks/useGuide";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

// ============================================================================
// ICON MAP
// ============================================================================

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Rocket,
  GraduationCap,
  Settings,
  FileEdit,
  Shield,
  BookOpen,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function FloatingHelpButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [question, setQuestion] = useState("");

  const { data: categories, isLoading: categoriesLoading } = useGuideCategories();
  const submitQuestion = useSubmitQuestion();

  // Non mostrare se non autenticato
  if (!user) return null;

  const handleSubmitQuestion = async () => {
    if (!question.trim()) {
      toast.error("Inserisci una domanda");
      return;
    }

    try {
      await submitQuestion.mutateAsync({
        question: question.trim(),
        contextPage: location.pathname,
      });
      toast.success(
        "Domanda registrata! Controlla la guida per risposte immediate, " +
        "oppure attendi una risposta via email per domande specifiche."
      );
      setQuestion("");
      setShowQuestionForm(false);
    } catch {
      toast.error("Errore nell'invio della domanda");
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="lg"
            className="rounded-full h-14 w-14 shadow-lg hover:shadow-xl transition-all"
            aria-label="Aiuto"
          >
            {open ? (
              <ChevronUp className="h-6 w-6" />
            ) : (
              <HelpCircle className="h-6 w-6" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="end"
          className="w-80 p-0"
          sideOffset={8}
        >
          {showQuestionForm ? (
            // Form per inviare domanda
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Fai una domanda</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuestionForm(false)}
                >
                  Indietro
                </Button>
              </div>
              <Textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Scrivi la tua domanda..."
                className="min-h-[100px] resize-none"
              />
              <Button
                onClick={handleSubmitQuestion}
                disabled={submitQuestion.isPending || !question.trim()}
                className="w-full"
              >
                {submitQuestion.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Invia domanda
              </Button>
              <p className="text-xs text-muted-foreground">
                La tua domanda verrà registrata. Se esiste già una risposta nella guida,
                consulta le categorie sopra. Per domande specifiche, un amministratore
                risponderà via email appena possibile.
              </p>
            </div>
          ) : (
            // Menu principale
            <div>
              {/* Header */}
              <div className="p-4 border-b">
                <h4 className="font-semibold">Come possiamo aiutarti?</h4>
                <p className="text-sm text-muted-foreground">
                  Consulta la guida o fai una domanda
                </p>
              </div>

              {/* Link alla guida completa */}
              <Link
                to="/guida"
                className="flex items-center gap-3 p-3 hover:bg-muted transition-colors"
                onClick={() => setOpen(false)}
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">Guida completa</p>
                  <p className="text-xs text-muted-foreground">
                    Tutte le istruzioni per usare il portale
                  </p>
                </div>
              </Link>

              <Separator />

              {/* Categorie */}
              {categoriesLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Caricamento...
                </div>
              ) : (
                <div className="py-2">
                  <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase">
                    Categorie
                  </p>
                  {categories?.slice(0, 4).map((cat) => {
                    const Icon = iconMap[cat.icon || "BookOpen"] || BookOpen;
                    return (
                      <Link
                        key={cat.id}
                        to={`/guida/${cat.slug}`}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-muted transition-colors"
                        onClick={() => setOpen(false)}
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{cat.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}

              <Separator />

              {/* Fai una domanda */}
              <button
                onClick={() => setShowQuestionForm(true)}
                className="flex items-center gap-3 p-3 w-full hover:bg-muted transition-colors text-left"
              >
                <div className="h-10 w-10 rounded-full bg-secondary/50 flex items-center justify-center">
                  <MessageCircleQuestion className="h-5 w-5 text-secondary-foreground" />
                </div>
                <div>
                  <p className="font-medium text-sm">Fai una domanda</p>
                  <p className="text-xs text-muted-foreground">
                    Non trovi quello che cerchi?
                  </p>
                </div>
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
